import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  Button,
  Card,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useState } from 'react';
import { useTranslate } from 'react-admin';
import { TeamQuickCreate } from '../teams/TeamQuickCreate';
import { formsSchema } from './formSchema';
import { InputText } from './InputText';

import { InputTextFormatter } from './InputTextFormatter';
import { SelectInput } from './SelectInput';

import InfoIcon from '@mui/icons-material/Info';
import { TotalPercentage } from '../../components/TotalPercentage';
import { customPalette } from '../../themes/customTheme';
import { InputCheckbox } from './InputCheckbox';

const MONTHS = [
  { id: 1, name: 'Enero' },
  { id: 2, name: 'Febrero' },
  { id: 3, name: 'Marzo' },
  { id: 4, name: 'Abril' },
  { id: 5, name: 'Mayo' },
  { id: 6, name: 'Junio' },
  { id: 7, name: 'Julio' },
  { id: 8, name: 'Agosto' },
  { id: 9, name: 'Septiembre' },
  { id: 10, name: 'Octubre' },
  { id: 11, name: 'Noviembre' },
  { id: 12, name: 'Diciembre' },
];

const useStyles = makeStyles({
  label: {
    fontWeight: 700,
    fontSize: '18px',
    lineHeight: '23px',
  },
  input: {
    '& .MuiFilledInput-root': {
      backgroundColor: 'white',
    },
    '& .MuiFormHelperText-root': {
      color: 'red',
    },
  },
  numInput: {
    '& .MuiFormHelperText-root': {
      color: 'red',
    },
  },
  card: {
    padding: '12px 24px',
  },
  fields: {
    padding: '8px 24px !important',
    borderRight: '1px solid #EDEDED',
  },

  tableTitle: {
    borderBottom: 'none',
    paddingLeft: '24px !important',
    paddingRight: '24px !important',
    borderRight: '1px solid #EDEDED',
    paddingBottom: '16px !important',
  },

  totalPercentage: {
    fontWeight: 600,
    fontSize: '20px',
    lineHeight: '25px',
    textAlign: 'center',
    verticalAlign: 'top',
    paddingTop: '8px !important',
  },

  limit: {
    opacity: '0',
    fontWeight: 500,
    fontSize: '11px',
    lineHeight: '11px',
    textAlign: 'center',
  },

  errorLimit: {
    opacity: '1',
    color: 'red',
  },

  totalBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    alignItems: 'center',
  },
});

const GoalHeader = ({ control, register }) => {
  const classes = useStyles();
  const translate = useTranslate();

  return (
    <Box sx={{ display: 'flex', gap: '48px', alignItems: 'flex-start' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Typography className={classes.label}>
          {translate('resources.goals.fields.month')}
        </Typography>
        <Box>
          <SelectInput
            name="month"
            options={MONTHS}
            label="Seleccione"
            sx={{ margin: 0, minWidth: '150px' }}
            control={control}
            register={register}
            validation={formsSchema.month}
          />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Typography className={classes.label}>Objetivo del mes</Typography>
        <Box sx={{ width: '100%', display: 'flex', gap: '16px' }}>
          <InputTextFormatter
            name="visits"
            control={control}
            register={register}
            validation={formsSchema.visit}
            label={translate('resources.goals.fields.visit')}
            className={classes.input}
            required={true}
          />
          <InputTextFormatter
            name="notes"
            label={translate('resources.goals.fields.notes')}
            control={control}
            register={register}
            validation={formsSchema.notes}
            className={classes.input}
            required={true}
          />

          <InputTextFormatter
            name="subscribers"
            label={translate('resources.goals.fields.subscribers')}
            control={control}
            register={register}
            validation={formsSchema.subscribers}
            className={classes.input}
            required={true}
          />
          <InputTextFormatter
            name="rfv"
            label={translate('resources.goals.fields.rfv')}
            className={classes.input}
            control={control}
            register={register}
            validation={formsSchema.rfv}
            required={true}
          />
        </Box>
      </Box>
    </Box>
  );
};

const NumInput = ({ name, control, register }) => {
  const classes = useStyles();

  return (
    <InputText
      type="number"
      variant="filled"
      name={name}
      label="Porcentaje"
      control={control}
      register={register}
      className={classes.numInput}
      // errors={errors}
    />
  );
};

export const Step1 = ({ setTeams, step, control, register, watch, errors }) => {
  const teams_goals = watch('teams_goals');
  const classes = useStyles();
  const translate = useTranslate();
  const [open, setOpen] = useState(false);
  const totalPercentageVisits = teams_goals?.reduce(
    (acc, team) => acc + Number(team?.percentage_visits || 0),
    0
  );

  const totalPercentageNotes = teams_goals?.reduce(
    (acc, team) => acc + Number(team?.percentage_notes || 0),
    0
  );

  const totalPercentageSubscribers = teams_goals?.reduce(
    (acc, team) => acc + Number(team?.percentage_subscribers || 0),
    0
  );

  const handleCreate = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmit = async item => {
    const news_teams = teams_goals || [];
    const to_insert = {
      team_id: item?.id,
      name: item?.name,
    };
    news_teams.push(to_insert);
    if (item) {
      setTeams(prev => [...prev, to_insert]);
    }
    setOpen(false);
  };

  if (step !== 1) return null;
  return (
    <Box>
      <GoalHeader errors={errors} control={control} register={register} />
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        marginBottom="8px"
        marginTop="48px"
      >
        <Typography className={classes.label}>
          {translate('resources.goals.create.titles.step1_subtitle')}
        </Typography>
        <Button
          variant="text"
          color="primary"
          onClick={() => handleCreate()}
          startIcon={<AddIcon />}
          sx={{
            textTransform: 'none !important',
            fontSize: '14px !important',
            lineHeight: '19px !important',
            fontWeight: '600 !important',
            letterSpacing: '0.5px !important',
            padding: '8px 22px!important',
            boxShadow: 'none !important',
          }}
        >
          {translate('resources.goals.fields.create_team')}
        </Button>
      </Box>

      <Card className={classes.card}>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell
                  align="left"
                  sx={{
                    borderBottom: 'none',
                    paddingRight: '24px !important',
                    paddingBottom: '16px !important',
                  }}
                >
                  {translate('resources.goals.fields.team')}
                </TableCell>
                <TableCell align="center" className={classes.tableTitle}>
                  {translate('resources.goals.fields.visit')}
                </TableCell>
                <TableCell align="center" className={classes.tableTitle}>
                  {translate('resources.goals.fields.notes')}
                </TableCell>
                <TableCell align="center" className={classes.tableTitle}>
                  {translate('resources.goals.fields.subscribers')}
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    borderBottom: 'none',
                    paddingLeft: '24px !important',
                    paddingBottom: '16px !important',
                  }}
                >
                  {translate('resources.goals.fields.rfv')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {teams_goals?.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell
                    align="left"
                    sx={{ paddingRight: '24px !important' }}
                  >
                    <Typography>{`${row?.name}`}</Typography>
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ verticalAlign: 'top' }}
                    className={classes.fields}
                  >
                    <NumInput
                      control={control}
                      errors={errors}
                      register={register}
                      name={`teams_goals.${index}.percentage_visits`}
                    />
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ verticalAlign: 'top' }}
                    className={classes.fields}
                  >
                    <NumInput
                      control={control}
                      register={register}
                      errors={errors}
                      name={`teams_goals.${index}.percentage_notes`}
                    />
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{ verticalAlign: 'top' }}
                    className={classes.fields}
                  >
                    <NumInput
                      control={control}
                      register={register}
                      errors={errors}
                      name={`teams_goals.${index}.percentage_subscribers`}
                    />
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      verticalAlign: 'top',
                      padding: '8px 0 8px 24px !important',
                    }}
                  >
                    <InputCheckbox
                      name={`teams_goals.${index}.percentage_rfv`}
                      control={control}
                      register={register}
                    />
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell
                  align="left"
                  sx={{ verticalAlign: 'top' }}
                ></TableCell>
                <TableCell>
                  <TotalPercentage
                    percentage={errors?.teams_goals?.[0]?.percentage_visits}
                    totalPercentage={totalPercentageVisits}
                    limitMessage="resources.goals.create.step1_error_limit"
                    errorMesage="resources.goals.validations.sumc100"
                  />
                </TableCell>
                <TableCell>
                  <TotalPercentage
                    percentage={errors?.teams_goals?.[0]?.percentage_notes}
                    totalPercentage={totalPercentageNotes}
                    limitMessage="resources.goals.create.step1_error_limit"
                    errorMesage="resources.goals.validations.sumc100"
                  />
                </TableCell>

                <TableCell>
                  <TotalPercentage
                    percentage={
                      errors?.teams_goals?.[0]?.percentage_subscribers
                    }
                    totalPercentage={totalPercentageSubscribers}
                    limitMessage="resources.goals.create.step1_error_limit"
                    errorMesage="resources.goals.validations.sumc100"
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <InfoIcon sx={{ color: customPalette.primary.main }} />
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 500,
              lineHeight: '19px',
              color: customPalette.primary.main,
            }}
          >
            {translate('resources.goals.create.step1_limit')}
          </Typography>
        </Box>
      </Card>
      <TeamQuickCreate
        open={open}
        handleClose={() => handleClose()}
        handleSubmit={i => handleSubmit(i)}
      />
    </Box>
  );
};
